import JSZip from 'jszip';

export const DOCUMENT_ACCEPT =
  '.pdf,.zip,application/pdf,application/zip,application/x-zip-compressed';

export const DOCUMENT_TYPE_ERROR =
  'Only PDF and ZIP files are accepted. Images, videos, and other types are not allowed.';

export const MIXED_DOCUMENT_ERROR =
  'Select either PDF files or one ZIP, not both. Multiple PDFs are packed into a single batch ZIP.';

export const MULTIPLE_ZIP_ERROR =
  'Only one ZIP can be uploaded at a time. Select a single ZIP, or select multiple PDFs to pack into one batch.';

function extensionOf(name) {
  const trimmed = String(name || '').trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return '';
  return trimmed.slice(dot).toLowerCase();
}

export function isPdfFile(file) {
  return Boolean(file && extensionOf(file.name) === '.pdf');
}

export function isZipFile(file) {
  return Boolean(file && extensionOf(file.name) === '.zip');
}

export function isAllowedDocumentFile(file) {
  if (!file || !file.name) return false;
  const ext = extensionOf(file.name);
  if (ext !== '.pdf' && ext !== '.zip') return false;
  const mime = String(file.type || '').split(';')[0].trim().toLowerCase();
  if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) {
    return false;
  }
  return true;
}

export function partitionDocumentFiles(fileList) {
  const accepted = [];
  const rejected = [];
  Array.from(fileList || [])
    .filter(Boolean)
    .forEach((file) => {
      if (isAllowedDocumentFile(file)) accepted.push(file);
      else rejected.push(file.name || 'unnamed file');
    });
  return { accepted, rejected };
}

function uniqueMemberName(name, used) {
  const raw = String(name || 'document.pdf').trim() || 'document.pdf';
  const key = raw.toLowerCase();
  if (!used.has(key)) {
    used.add(key);
    return raw;
  }
  const dot = raw.lastIndexOf('.');
  const stem = dot > 0 ? raw.slice(0, dot) : raw;
  const ext = dot > 0 ? raw.slice(dot) : '';
  let n = 2;
  let candidate = `${stem} (${n})${ext}`;
  while (used.has(candidate.toLowerCase())) {
    n += 1;
    candidate = `${stem} (${n})${ext}`;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

export function batchZipName(pdfs) {
  const first = String(pdfs[0]?.name || 'batch').replace(/\.pdf$/i, '').trim() || 'batch';
  const safe = first.replace(/[^\w.-]+/g, '_').slice(0, 80);
  return `${safe}-batch.zip`;
}

function readFileBytes(file) {
  if (file && typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error(`Unable to read ${file?.name || 'file'}`));
    reader.readAsArrayBuffer(file);
  });
}

async function zipPdfsIntoBatch(pdfs) {
  const zip = new JSZip();
  const used = new Set();
  const memberNames = [];
  for (const file of pdfs) {
    const member = uniqueMemberName(file.name, used);
    memberNames.push(member);
    zip.file(member, await readFileBytes(file));
  }
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const zipName = batchZipName(pdfs);
  const zipFile = new File([blob], zipName, { type: 'application/zip' });
  return { file: zipFile, memberNames };
}

export function planDocumentFiles(files) {
  const list = Array.from(files || []).filter(Boolean);
  const pdfs = list.filter(isPdfFile);
  const zips = list.filter(isZipFile);
  if (zips.length && pdfs.length) {
    return { action: 'error', files: [], error: MIXED_DOCUMENT_ERROR };
  }
  if (zips.length > 1) {
    return { action: 'error', files: [], error: MULTIPLE_ZIP_ERROR };
  }
  if (pdfs.length > 1) {
    return { action: 'zip', files: pdfs, error: null };
  }
  return { action: 'keep', files: list, error: null };
}

export async function clubDocumentFiles(files) {
  const plan = planDocumentFiles(files);
  if (plan.action === 'error') {
    return { files: [], memberNames: null, error: plan.error };
  }
  if (plan.action === 'keep') {
    return { files: plan.files, memberNames: null, error: null };
  }
  const packed = await zipPdfsIntoBatch(plan.files);
  return { files: [packed.file], memberNames: packed.memberNames, error: null };
}
